// client/components/evaluate/EvaluateRuleCard.tsx
// Task 4.3 — Main rule card composing EvidencePanel, ResolvedFromScopeBadge, StaleBadge
import React, { useState } from 'react';
import type { FairRuleResult } from '../../hooks/useEvaluateData';
import { EvidencePanel } from './EvidencePanel';
import { ResolvedFromScopeBadge } from './ResolvedFromScopeBadge';
import { StaleBadge } from './StaleBadge';
import { IPillarTierBadge, resolveIPillarTier } from './IPillarTierBadge';
import { TierTwoMappingsOverlay } from './TierTwoMappingsOverlay';

interface EvaluateRuleCardProps {
  rule: FairRuleResult;
  currentEngineVersion: string;
  onAccept: (rule: FairRuleResult) => void;
  onReject: (rule: FairRuleResult) => void;
  onEdit: (rule: FairRuleResult) => void;
  defaultExpanded?: boolean;
}

// Cream/teal tokens (v2.0) — four distinct family hues for the pillar badges.
const PILLAR_COLORS: Record<string, string> = {
  F: 'var(--tri-teal)', A: 'var(--tri-good)', I: 'var(--tri-soft)', R: 'var(--tri-terra)',
};

const STATUS_COLORS = {
  PASS:           { bg: 'var(--tri-good-bg)', fg: 'var(--tri-good)', border: 'var(--tri-good)' },
  FAIL:           { bg: 'var(--tri-hard-bg)', fg: 'var(--tri-hard)', border: 'var(--tri-hard-line)' },
  NOT_APPLICABLE: { bg: 'var(--tri-neutral-bg)', fg: 'var(--tri-neutral)', border: 'var(--tri-neutral)' },
} as const;

// Tier badge palette for tiered rules (F2 today). Mirrors the PASS/FAIL chip
// width so the cards stay visually aligned regardless of which mode the rule
// is in.
const TIER_COLORS: Record<number, { bg: string; fg: string; border: string }> = {
  0: { bg: 'var(--tri-hard-bg)', fg: 'var(--tri-hard)', border: 'var(--tri-hard-line)' },
  1: { bg: 'var(--tri-soft-bg)', fg: 'var(--tri-soft)', border: 'var(--tri-soft)' },
  2: { bg: 'var(--tri-neutral-bg)', fg: 'var(--tri-neutral)', border: 'var(--tri-neutral)' },
  3: { bg: 'var(--tri-good-bg)', fg: 'var(--tri-good)', border: 'var(--tri-good)' },
};

const TIER_LABELS: Record<number, string> = {
  0: 'Below Tier 1',
  1: 'Tier 1 — Minimal',
  2: 'Tier 2 — Citation',
  3: 'Tier 3 — Descriptive',
};

export function EvaluateRuleCard({
  rule,
  currentEngineVersion,
  onAccept,
  onReject,
  onEdit,
  defaultExpanded = true,
}: EvaluateRuleCardProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [tier2OverlayOpen, setTier2OverlayOpen] = useState(false);
  const decision = rule.evidence_structured.human_decision;
  const colors = STATUS_COLORS[rule.status];
  const pillarColor = PILLAR_COLORS[rule.pillar] ?? 'var(--tri-slate)';
  const iPillarTier = resolveIPillarTier(
    rule.rule,
    rule.evidence_structured.method,
    rule.status,
  );
  const isTier2Inferred = iPillarTier === 2;

  // Tier-bearing rules show their tier as the primary verdict pill, with the
  // binary PASS/FAIL collapse alongside it. Human override (Edit/Reject with a
  // tier override) takes precedence over the machine-computed tier.
  const breakdown = rule.evidence_structured.maturity_breakdown;
  const humanTier = decision?.maturity_decision?.asserted_tier;
  const effectiveTier =
    typeof humanTier === 'number' ? humanTier : breakdown?.tier_achieved;
  const isTiered = breakdown !== undefined;
  const tierColors =
    typeof effectiveTier === 'number'
      ? TIER_COLORS[effectiveTier] ?? TIER_COLORS[0]
      : null;

  return (
    <div
      id={`rule-${rule.rule}`}
      style={{
        background: 'var(--tri-card)',
        border: `1px solid ${colors.border}`,
        borderLeft: decision ? '3px solid var(--tri-soft)' : `1px solid ${colors.border}`,
        borderRadius: 8,
        padding: 14,
        marginBottom: 10,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flex: 1,
            background: 'transparent',
            border: 0,
            padding: 0,
            cursor: 'pointer',
            textAlign: 'left',
          }}
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 28,
              height: 28,
              borderRadius: 9999,
              background: pillarColor,
              color: '#fff',
              fontSize: '0.75rem',
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {rule.pillar}
          </span>
          <span style={{ flex: 1, color: 'var(--tri-ink)' }}>
            <span style={{ fontWeight: 600 }}>{rule.rule}</span>
            <span style={{ marginLeft: 8, color: 'var(--tri-slate)' }}>· {rule.ruleName}</span>
          </span>
          {isTiered && tierColors && typeof effectiveTier === 'number' ? (
            <span
              title={TIER_LABELS[effectiveTier]}
              style={{
                padding: '2px 8px',
                borderRadius: 9999,
                background: tierColors.bg,
                color: tierColors.fg,
                border: `1px solid ${tierColors.border}`,
                fontSize: '0.7rem',
                fontWeight: 700,
                letterSpacing: '0.04em',
                flexShrink: 0,
              }}
            >
              {effectiveTier === 0
                ? 'Below Tier 1'
                : `Tier ${effectiveTier}/${breakdown?.tier_max ?? 3}`}
            </span>
          ) : (
            <span
              style={{
                padding: '2px 8px',
                borderRadius: 9999,
                background: colors.bg,
                color: colors.fg,
                border: `1px solid ${colors.border}`,
                fontSize: '0.7rem',
                fontWeight: 700,
                letterSpacing: '0.04em',
                flexShrink: 0,
              }}
            >
              {rule.status}
            </span>
          )}
          <IPillarTierBadge
            ruleId={rule.rule}
            method={rule.evidence_structured.method}
            status={rule.status}
          />
          {decision && (
            <StaleBadge decision={decision} currentEngineVersion={currentEngineVersion} />
          )}
        </button>

        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <ActionChip label="&#10003; Accept" onClick={() => onAccept(rule)} color="var(--tri-good)" />
          <ActionChip label="&#10007; Reject" onClick={() => onReject(rule)} color="var(--tri-hard)" />
          <ActionChip label="&#9998; Edit"   onClick={() => onEdit(rule)}   color="var(--tri-neutral)" />
        </div>
      </div>

      {decision && (
        <div style={{ marginTop: 8 }}>
          <ResolvedFromScopeBadge decision={decision} />
          {decision.rationale && (
            <div style={{ marginTop: 6, color: 'var(--tri-body)', fontSize: '0.8rem' }}>
              <span style={{ color: 'var(--tri-slate)' }}>Rationale: </span>{decision.rationale}
            </div>
          )}
        </div>
      )}

      <div style={{ marginTop: 8, color: 'var(--tri-ink)', fontSize: '0.92rem', lineHeight: 1.5, fontWeight: 500 }}>
        {rule.description}
      </div>

      {isTier2Inferred && (
        <div style={{ marginTop: 6 }}>
          <button
            type="button"
            onClick={() => setTier2OverlayOpen(true)}
            style={{
              padding: 0,
              background: 'transparent',
              border: 0,
              color: 'var(--tri-terra)',
              fontSize: '0.8rem',
              fontWeight: 600,
              cursor: 'pointer',
              textDecoration: 'underline',
              textUnderlineOffset: '2px',
            }}
          >
            View inferred mappings →
          </button>
        </div>
      )}

      <div style={{ marginTop: 6, fontSize: '0.72rem', color: 'var(--tri-slate)', fontFamily: 'JetBrains Mono, Consolas, monospace' }}>
        method: <span style={{ color: 'var(--tri-teal)' }}>{rule.evidence_structured.method}</span>
        {' · evaluatedBy: '}<span style={{ color: 'var(--tri-neutral)' }}>{rule.evaluatedBy}</span>
      </div>

      <EvidencePanel
        evidence={rule.evidence_structured}
        expanded={expanded}
        noteText={rule.evidence || rule.description || undefined}
        showLegacyShaclPassNote={
          rule.status === 'PASS'
          && ['F2', 'F3', 'A1', 'R1'].includes(rule.rule)
          && (rule.evidence_structured.signals?.length ?? 0) === 0
          && (rule.evidence_structured.fallbacks_tried?.length ?? 0) === 0
          && rule.evidence_structured.maturity_breakdown === undefined
          // v1.1.8 enrichers always set threshold + measured. Their absence
          // distinguishes a true legacy PASS (engine pre-1.1.8) from a
          // fresh PASS where the input was simply sparse.
          && !rule.evidence_structured.threshold
          && !rule.evidence_structured.measured
        }
      />

      {isTier2Inferred && (rule.rule === 'I2' || rule.rule === 'I4') && (
        <TierTwoMappingsOverlay
          ruleId={rule.rule as 'I2' | 'I4'}
          isOpen={tier2OverlayOpen}
          onClose={() => setTier2OverlayOpen(false)}
        />
      )}
    </div>
  );
}

function ActionChip({ label, onClick, color }: { label: string; onClick: () => void; color: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '4px 10px',
        borderRadius: 9999,
        background: 'transparent',
        color,
        border: `1px solid ${color}`,
        fontSize: '0.72rem',
        fontWeight: 600,
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );
}
